import { CourseCardOverlay } from '@/components/catalog/CourseCardOverlay'
import { CourseCardSkeletonGrid } from '@/components/catalog/CourseCardSkeletonGrid'
import { CatalogPagination } from './CatalogPagination'
import { SortDropdown } from './filters/SortDropdown'
import { CatalogEmptyResults } from './CatalogEmptyResults'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@teach/ui'
import { cn } from '@/lib/utils'
import { academyCourseUrl } from '@/lib/academyLinks'
import type { ExploreSort } from '../catalogApi'
import type { Course } from '@/types/course'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { CARD_GRID_CLASS } from '@/components/catalog/cardGrid'

const GRID = CARD_GRID_CLASS

interface Props {
  items: Course[]
  total: number
  page: number
  totalPages: number
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  hasActiveFilters: boolean
  hasRemovableChips: boolean
  sort: ExploreSort
  onSortChange: (sort: ExploreSort) => void
  onRetry: () => void
  onReset: () => void
  onShowAllLanguages: () => void
  onPageChange: (page: number) => void
}

export const CatalogResults = ({
  items,
  total,
  page,
  totalPages,
  isLoading,
  isFetching,
  isError,
  hasActiveFilters,
  hasRemovableChips,
  sort,
  onSortChange,
  onRetry,
  onReset,
  onShowAllLanguages,
  onPageChange,
}: Props) => {
  const m = useMessages(messages)
  if (isLoading) {
    return <CourseCardSkeletonGrid count={10} className={GRID} />
  }

  if (isError) {
    return (
      <EmptyState
        title={m.loadFailedTitle}
        description={m.loadFailedHint}
        action={<Button onClick={onRetry}>{m.retry}</Button>}
      />
    )
  }

  if (items.length === 0) {
    return (
      <CatalogEmptyResults
        hasActiveFilters={hasActiveFilters}
        hasRemovableChips={hasRemovableChips}
        onReset={onReset}
        onShowAllLanguages={onShowAllLanguages}
      />
    )
  }

  return (
    <>
      {total > 0 ? (
        // Счётчик слева, сортировка — справа в углу.
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{m.coursesCount(total)}</span>
          <SortDropdown value={sort} onChange={onSortChange} />
        </div>
      ) : null}
      <div
        className={cn(GRID, 'transition-opacity', isFetching && 'opacity-60 pointer-events-none')}
      >
        {items.map((c) => (
          <CourseCardOverlay
            key={c.id}
            id={c.id}
            externalHref={c.slug ? academyCourseUrl(c.slug) : undefined}
            name={c.name}
            nameEn={c.nameEn}
            banner={c.banner}
            rating={c.rating?.value}
            updatedAt={c.updatedAt}
            completedCount={c.amountOfPeopleCompleted ?? 0}
            variant="catalog"
          />
        ))}
      </div>
      <CatalogPagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
    </>
  )
}
